import { useCallback, useState } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import type { TrackDefinition } from '@internal/backstage-plugin-pulse-common';
import { invalidateTracksCache } from './cache';

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Updates an existing UI-managed Pulse track definition. */
export const useUpdateTrack = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(
    async (
      trackId: string,
      track: TrackDefinition,
    ): Promise<TrackDefinition> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `plugin://pulse/tracks/${encodeURIComponent(trackId)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(track),
          },
        );
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Failed to update track: ${response.status}`,
          );
        }
        const updated = (await response.json()) as TrackDefinition;
        invalidateTracksCache();
        return updated;
      } catch (error_) {
        const requestError = error_ as Error;
        setError(requestError);
        throw requestError;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { update, loading, error };
};
